import { useQueries } from '@tanstack/react-query';
import { Box, Flex, Heading, SimpleGrid, Stack, Text } from '@chakra-ui/react';
import { Eye, Loader2, ShieldOff } from 'lucide-react';

import { getHealth, getStats, getSummary } from '../api/client';
import type { Stats, Summary } from '../api/types';
import { count, relativeTime } from '../lib/format';

// WatchlistView is the home of heat-model near-misses. These are not
// "rejected by X"; they are signals with enough evidence to watch but
// not enough heat/reliability to call confirmed.

export function WatchlistView() {
  const [healthQuery, statsQuery, summaryQuery] = useQueries({
    queries: [
      {
        queryKey: ['health'],
        queryFn: ({ signal }) => getHealth({ signal }),
        refetchInterval: 5_000,
      },
      {
        queryKey: ['stats'],
        queryFn: ({ signal }) => getStats({ signal }),
        refetchInterval: 5_000,
      },
      {
        queryKey: ['summary'],
        queryFn: ({ signal }) => getSummary({ signal }),
        refetchInterval: 5_000,
      },
    ],
  });
  const stats: Stats | undefined = statsQuery.data ?? healthQuery.data?.stats;
  const summary: Summary | undefined = summaryQuery.data;
  const isFetching = healthQuery.isFetching || statsQuery.isFetching || summaryQuery.isFetching;
  const isError = healthQuery.isError || statsQuery.isError || summaryQuery.isError;

  const nearMisses = stats?.watchlist ?? 0;

  return (
    <Stack as="section" mt="8" gap="6">
      <Flex
        flexDir={{ base: 'column', xl: 'row' }}
        gap="3"
        align={{ xl: 'flex-end' }}
        justify={{ xl: 'space-between' }}
      >
        <Stack gap="3" maxW="3xl">
          <Flex align="center" gap="3">
            <Box color="signal.blue">
              <Eye size={28} />
            </Box>
            <Heading as="h1" fontSize="3xl" fontWeight="semibold" letterSpacing="tight">
              Watchlist
            </Heading>
          </Flex>
          <Text fontSize={{ base: 'sm', md: 'md' }} color="text.muted">
            Signals with heat 45-64. They have some evidence, but need better
            tail confirmation, liquidity quality, capital confirmation, or move strength.
          </Text>
        </Stack>
        {isFetching ? (
          <Box
            display="inline-flex"
            alignItems="center"
            gap="2"
            borderRadius="oqPanel"
            px="4"
            py="2.5"
            layerStyle="glassPanel"
          >
            <Box as="span" display="inline-flex" alignItems="center">
              <Loader2 className="oq-spin" size={16} />
            </Box>
            <Text as="span" fontSize="sm" color="text.muted">
              Refreshing
            </Text>
          </Box>
        ) : null}
      </Flex>

      {isError ? (
        <Box
          mt="3"
          borderWidth="1px"
          borderColor="signal.warn/20"
          bg="signal.warn/10"
          px="4"
          py="3"
          borderRadius="oqPanel"
        >
          <Text fontSize="sm" color="signal.warn">
            Watchlist is unavailable. Backend didn&apos;t respond.
          </Text>
        </Box>
      ) : null}

      <SimpleGrid gap="4" columns={{ base: 1, md: 3 }}>
        <WatchlistMetric
          label="Watchlist"
          value={count(nearMisses)}
          detail="Heat 45-64. Not confirmed, but worth watching."
          tone="warn"
        />
        <WatchlistMetric
          label="Confirmed"
          value={count(stats?.confirmed ?? 0)}
          detail="Heat 65+. These are the strict feed."
          tone="good"
        />
        <WatchlistMetric
          label="Total scoring attempts"
          value={count(stats?.scoring_attempts ?? 0)}
          detail="Per poll, per asset. Watchlist / confirmed is the strictness ratio."
          tone="blue"
        />
      </SimpleGrid>

      <Box mt="6" borderRadius="oqPanel" p="5" layerStyle="glassPanel">
        <Flex align="flex-start" gap="3">
          <Box color="text.muted" mt="0.5">
            <ShieldOff size={20} />
          </Box>
          <Box minW="0" flex="1">
            <Heading as="h2" fontSize="md" fontWeight="semibold">
              Why are individual markets not listed yet?
            </Heading>
            <Text mt="1" fontSize="sm" color="text.muted">
              The engine exposes watchlist items through the same mover feed when Mode is
              set to Watchlist. This page keeps the operational count, while the dashboard
              mode lets you inspect the current watchlist rows directly.
            </Text>
            <Text mt="3" fontSize="sm" color="text.muted">
              Watchlist language should explain the weakest evidence in plain English:
              needs tail confirmation, thin liquidity, small move, or volume not exceptional.
            </Text>
            <Flex mt="4" flexWrap="wrap" align="center" gap="4" fontSize="xs" color="text.muted">
              {summary?.last_message_at ? (
                <span>Last backend message {relativeTime(summary.last_message_at)}</span>
              ) : null}
              {stats?.last_message_at ? (
                <span>Stats refreshed {relativeTime(stats.last_message_at)}</span>
              ) : null}
            </Flex>
          </Box>
        </Flex>
      </Box>
    </Stack>
  );
}

function WatchlistMetric({
  label,
  value,
  detail,
  tone,
}: {
  label: string;
  value: string;
  detail: string;
  tone: 'good' | 'warn' | 'blue';
}) {
  const ringColor =
    tone === 'good' ? 'signal.good/15' : tone === 'warn' ? 'signal.warn/15' : 'signal.blue/15';
  const valueColor = tone === 'good' ? 'signal.good' : tone === 'warn' ? 'signal.warn' : 'text.primary';
  return (
    <Box
      borderRadius="oqPanel"
      p="4"
      layerStyle="glassPanel"
      outline="1px solid"
      outlineColor={ringColor}
    >
      <Text fontSize="xs" textTransform="uppercase" letterSpacing="wide" color="text.muted">
        {label}
      </Text>
      <Text mt="2" fontSize="3xl" fontWeight="semibold" fontVariantNumeric="tabular-nums" color={valueColor}>
        {value}
      </Text>
      <Text mt="2" fontSize="xs" color="text.muted">
        {detail}
      </Text>
    </Box>
  );
}